<template>
    <div class="admin-owner-details p-2">
        <LoaderComponent v-if="loading" />
        <div class="alert alert-danger" v-else-if="error">{{ error }}</div>
        <div v-else-if="ownerDetails && subscriptions" class="admin-owner-details-content">
            <div class="owner-details-header">
                <div>
                    <div class="owner-details-nav">
                        <router-link class="owner-details-back" :to="{ name: 'admin.owners' }">
                            <feather type="arrow-left" />
                            Comptes clients
                        </router-link>
                        <span class="owner-details-kicker">Administration</span>
                    </div>
                    <div class="owner-details-title-row">
                        <h5 class="title mb-0">{{ ownerDisplayName }}</h5>
                        <span class="badge owner-details-status" :class="ownerStatusClass">{{ ownerStatusLabel }}</span>
                    </div>
                    <a class="owner-details-email" :href="`mailto:${ownerDetails.email}`">{{ ownerDetails.email }}</a>
                </div>

                <div class="owner-details-header-actions">
                    <button
                        v-if="!ownerDetails.has_active_subscription"
                        type="button"
                        class="btn btn-sm btn-outline-secondary btn-circle"
                        @click="toggleArchiveAccount"
                        :disabled="isTogglingArchiveAccount">
                        <feather :type="ownerDetails.enabled_by_admin ? 'pause-circle' : 'play-circle'" />
                        {{ ownerDetails.enabled_by_admin ? "Désactiver le compte" : "Réactiver temporairement" }}
                    </button>
                    <router-link
                        v-if="isYservices || ownerDetails.has_manual_billing"
                        class="btn btn-sm btn-success btn-circle"
                        :to="{ name: 'admin.owners.licences.create' }"
                        :class="{ disabled: !isYservices && !canUpdateSubscription }">
                        <feather type="plus" />
                        Ajouter des licences
                    </router-link>
                </div>
            </div>

            <div class="owner-details-summary">
                <div class="owner-details-summary-card">
                    <span>Licences</span>
                    <strong>{{ subscriptions.length }}</strong>
                    <small>{{ ownerDetails.has_active_subscription ? "Compte actif" : "Aucune licence active" }}</small>
                </div>
                <div class="owner-details-summary-card">
                    <span>Établissements</span>
                    <strong>{{ restaurantsCount }}</strong>
                    <small>{{ restaurantsCount > 0 ? "Configuré" : "Aucun établissement" }}</small>
                </div>
                <div class="owner-details-summary-card">
                    <span>Employés</span>
                    <strong>{{ employeesCount }}</strong>
                    <small>{{ isNoShow ? "Comptes rattachés" : "Non disponible" }}</small>
                </div>
                <div class="owner-details-summary-card">
                    <span>Dernière connexion</span>
                    <strong>{{ lastLoginDate }}</strong>
                    <small>{{ ownerDetails.has_manual_billing ? "Facturation manuelle" : "Facturation standard" }}</small>
                </div>
            </div>

            <div v-if="!ownerDetails.had_active_subscription" class="owner-details-notice">
                <feather type="alert-triangle" />
                Ce restaurateur n'a jamais payé de licence.
            </div>

            <section class="owner-details-card">
                <ShowOwnerDetails
                    v-if="!isEditingOwner"
                    :ownerDetails="ownerDetails"
                    @edit-owner="isEditingOwner = true"
                    @owner-deleted="$router.push('/')" />
                <EditOwner
                    v-else
                    :ownerId="owner_id"
                    @cancel-edit="isEditingOwner = false"
                    @edited="
                        isEditingOwner = false;
                        fetchOwnerDetails();
                    " />
            </section>

            <section class="owner-details-card owner-details-collapsible">
                <button
                    type="button"
                    class="owner-details-section-toggle"
                    :aria-expanded="isSectionOpen('licenses')"
                    @click="toggleSection('licenses')">
                    <div>
                        <h6>Licences</h6>
                        <p v-if="ownerDetails.has_manual_billing" class="mb-0">
                            <feather type="alert-triangle" class="text-warning" />
                            Facturation manuelle
                        </p>
                        <p v-else class="mb-0">Suivi des licences et rattachements établissements.</p>
                    </div>
                    <div class="owner-details-toggle-meta">
                        <span class="owner-details-count">{{ subscriptions.length }}</span>
                        <feather class="owner-details-chevron" :class="{ open: isSectionOpen('licenses') }" type="chevron-down" />
                    </div>
                </button>

                <div v-show="isSectionOpen('licenses')" class="owner-details-section-body">
                    <div class="owner-details-empty" v-if="subscriptions.length === 0">Ce restaurateur ne possède aucune licence.</div>
                    <div v-else class="owner-details-subscriptions">
                        <ShowSubscriptionDetails
                            v-for="subscription in subscriptions"
                            :key="subscription.id"
                            @subscription-canceled="onSubscriptionCanceled"
                            @subscription-assigned="onSubscriptionAssigned"
                            :subscription="subscription"
                            :restaurants="ownerDetails.restaurants.data"
                            :has-manual-billing="ownerDetails.has_manual_billing"
                            class="owner-details-subscription" />
                    </div>
                </div>
            </section>

            <section class="owner-details-card owner-details-collapsible" v-if="isNoShow">
                <button
                    type="button"
                    class="owner-details-section-toggle"
                    :aria-expanded="isSectionOpen('invoices')"
                    @click="toggleSection('invoices')">
                    <div>
                        <h6>Factures</h6>
                        <p class="mb-0">Historique de facturation du compte.</p>
                    </div>
                    <feather class="owner-details-chevron" :class="{ open: isSectionOpen('invoices') }" type="chevron-down" />
                </button>
                <div v-show="isSectionOpen('invoices')" class="owner-details-section-body">
                    <Invoices :isComponent="true" :user_id="ownerDetails.id" />
                </div>
            </section>

            <section class="owner-details-card owner-details-collapsible">
                <button type="button" class="owner-details-section-toggle" :aria-expanded="isSectionOpen('sms')" @click="toggleSection('sms')">
                    <div>
                        <h6>Liste des SMS</h6>
                        <p class="mb-0">Historique des messages envoyés à ce compte.</p>
                    </div>
                    <feather class="owner-details-chevron" :class="{ open: isSectionOpen('sms') }" type="chevron-down" />
                </button>
                <div v-show="isSectionOpen('sms')" class="owner-details-section-body">
                    <ShowOwnerSmsList :ownerDetails="ownerDetails" :show-header="false" :default-open="true" />
                </div>
            </section>

            <section class="owner-details-card owner-details-collapsible">
                <button
                    type="button"
                    class="owner-details-section-toggle"
                    :aria-expanded="isSectionOpen('restaurants')"
                    @click="toggleSection('restaurants')">
                    <div>
                        <h6>Établissements</h6>
                        <p class="mb-0">Configuration et options des établissements rattachés.</p>
                    </div>
                    <div class="owner-details-toggle-meta">
                        <span class="owner-details-count">{{ restaurantsCount }}</span>
                        <feather class="owner-details-chevron" :class="{ open: isSectionOpen('restaurants') }" type="chevron-down" />
                    </div>
                </button>
                <div v-show="isSectionOpen('restaurants')" class="owner-details-section-body">
                    <div class="owner-details-scroll">
                        <ShowOwnerRestaurants
                            :ownerDetails="ownerDetails"
                            :subscriptions="subscriptions"
                            :show-header="false"
                            :default-open="true"
                            @restaurant-deleted="fetchOwnerDetails" />
                    </div>
                </div>
            </section>

            <section class="owner-details-card owner-details-collapsible" v-if="isNoShow">
                <button
                    type="button"
                    class="owner-details-section-toggle"
                    :aria-expanded="isSectionOpen('employees')"
                    @click="toggleSection('employees')">
                    <div>
                        <h6>Employés</h6>
                        <p class="mb-0">Comptes employés rattachés au restaurateur.</p>
                    </div>
                    <div class="owner-details-toggle-meta">
                        <span class="owner-details-count">{{ employeesCount }}</span>
                        <feather class="owner-details-chevron" :class="{ open: isSectionOpen('employees') }" type="chevron-down" />
                    </div>
                </button>
                <div v-show="isSectionOpen('employees')" class="owner-details-section-body">
                    <ShowOwnerEmployees
                        :employees="ownerDetails.employees.data"
                        :show-header="false"
                        :default-open="true"
                        @employee-deleted="fetchOwnerDetails" />
                </div>
            </section>

            <section class="owner-details-card owner-details-collapsible" v-if="isNoShow">
                <button type="button" class="owner-details-section-toggle" :aria-expanded="isSectionOpen('cgvs')" @click="toggleSection('cgvs')">
                    <div>
                        <h6>CGV / CGU / CP acceptées</h6>
                        <p class="mb-0">Documents acceptés et traces d’envoi.</p>
                    </div>
                    <div class="owner-details-toggle-meta">
                        <span class="owner-details-count">{{ acceptedCgvsCount }}</span>
                        <feather class="owner-details-chevron" :class="{ open: isSectionOpen('cgvs') }" type="chevron-down" />
                    </div>
                </button>
                <div v-show="isSectionOpen('cgvs')" class="owner-details-section-body">
                    <ShowOwnerAcceptedCgvs :cgvs="ownerDetails.accepted_cgvs.data" :show-header="false" :default-open="true" />
                </div>
            </section>

            <section class="owner-details-card owner-details-collapsible">
                <button
                    type="button"
                    class="owner-details-section-toggle"
                    :aria-expanded="isSectionOpen('activity')"
                    @click="toggleSection('activity')">
                    <div>
                        <h6>Activité mensuelle</h6>
                        <p class="mb-0">Volume créé mois par mois, filtrable par module.</p>
                    </div>
                    <div class="owner-details-toggle-meta">
                        <span class="owner-details-count">{{ activityTotal }}</span>
                        <feather class="owner-details-chevron" :class="{ open: isSectionOpen('activity') }" type="chevron-down" />
                    </div>
                </button>

                <div v-show="isSectionOpen('activity')" class="owner-details-section-body">
                    <div class="owner-activity-toolbar">
                        <div class="owner-activity-modules">
                            <button
                                v-for="module in activityModules"
                                :key="module.key"
                                type="button"
                                class="owner-activity-module"
                                :class="{ active: selectedActivityModules.includes(module.key) }"
                                @click="toggleActivityModule(module.key)">
                                {{ module.label }}
                            </button>
                        </div>
                        <div class="owner-activity-actions">
                            <label class="form-label owner-activity-curve-toggle">
                                <input v-model="showCurveChart" type="checkbox" />
                                <span>Courbes</span>
                            </label>
                            <select
                                v-model.number="activityMonths"
                                class="form-select form-select-sm owner-activity-months select-native mb-0"
                                @change="fetchOwnerActivity">
                                <option :value="6">6 mois</option>
                                <option :value="12">12 mois</option>
                                <option :value="24">24 mois</option>
                                <option :value="36">36 mois</option>
                            </select>
                        </div>
                    </div>

                    <LoaderComponent v-if="ownerActivityLoading" />
                    <div v-else-if="activityRows.length === 0" class="owner-details-empty">Aucune activité sur la période.</div>
                    <div v-else class="owner-activity-chart-card">
                        <VChart class="owner-activity-chart" :option="activityChartOption" autoresize />
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import axios from "axios";
import { use } from "echarts/core";
import { BarChart, LineChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import VChart from "vue-echarts";
import LoaderComponent from "../../components/LoaderComponent.vue";
import ShowOwnerRestaurants from "../../components/Admin/ShowOwnerRestaurants.vue";
import ShowOwnerSmsList from "../../components/Admin/ShowOwnerSmsList.vue";
import ShowOwnerEmployees from "../../components/Admin/ShowOwnerEmployees.vue";
import ShowOwnerAcceptedCgvs from "../../components/Admin/ShowOwnerAcceptedCgvs.vue";
import ShowOwnerDetails from "../../components/Admin/ShowOwnerDetails.vue";
import ShowSubscriptionDetails from "../../components/Admin/ShowSubscriptionDetails.vue";
import Invoices from "../Default/Licences/Invoices.vue";
import EditOwner from "../../components/Admin/Owners/Edit.vue";

use([CanvasRenderer, BarChart, LineChart, GridComponent, LegendComponent, TooltipComponent]);

export default {
    data() {
        return {
            loading: 0,
            error: null,
            ownerDetails: null,
            moment,
            stripeSubscriptions: [],
            stripeSubscriptionsInvoices: [],
            isEditingOwner: false,
            isTogglingArchiveAccount: false,
            ownerActivityLoading: false,
            ownerActivity: null,
            activityMonths: 12,
            activityModules: [
                { key: "reservations", label: "Réservations", color: "#157ec2" },
                { key: "orders", label: "Commandes", color: "#44a475" },
                { key: "gift_vouchers", label: "Bons cadeaux", color: "#e2a034" },
                { key: "campaigns", label: "Campagnes", color: "#8c63d9" },
                { key: "ordering_table", label: "Commande à table", color: "#d85f6a" },
            ],
            selectedActivityModules: ["reservations", "orders", "gift_vouchers", "campaigns", "ordering_table"],
            showCurveChart: false,
            openSections: {
                activity: true,
                licenses: true,
                invoices: false,
                sms: false,
                restaurants: false,
                employees: false,
                cgvs: false,
            },
        };
    },
    computed: {
        rights() {
            return this.$store.getters["users/formattedRights"];
        },
        canUpdateSubscription() {
            return this.rights.includes("admin.subscription.update");
        },
        owner_id() {
            return this.$route.params.owner_id;
        },
        subscriptions() {
            var subscriptions = [];

            for (var key in this.stripeSubscriptions) {
                subscriptions = subscriptions.concat(this.stripeSubscriptions[key]);
            }

            return subscriptions;
        },
        ownerDisplayName() {
            if (!this.ownerDetails) {
                return "";
            }

            const fullname = `${this.ownerDetails.firstname || ""} ${this.ownerDetails.lastname || ""}`.trim();

            return fullname || this.ownerDetails.company || this.ownerDetails.email || `Client #${this.ownerDetails.id}`;
        },
        ownerStatusLabel() {
            if (!this.ownerDetails) {
                return "";
            }

            if (this.ownerDetails.enabled_by_admin) {
                return "Activé admin";
            }

            if (this.ownerDetails.has_active_subscription) {
                return "Actif";
            }

            return "Inactif";
        },
        ownerStatusClass() {
            if (!this.ownerDetails) {
                return "";
            }

            if (this.ownerDetails.enabled_by_admin) {
                return "text-bg-danger";
            }

            if (this.ownerDetails.has_active_subscription) {
                return "text-bg-success";
            }

            return "text-bg-warning";
        },
        restaurantsCount() {
            return this.ownerDetails?.restaurants?.data?.length || 0;
        },
        employeesCount() {
            return this.ownerDetails?.employees?.data?.length || 0;
        },
        acceptedCgvsCount() {
            return this.ownerDetails?.accepted_cgvs?.data?.length || 0;
        },
        lastLoginDate() {
            if (!this.ownerDetails?.last_login_at) {
                return "-";
            }

            return this.displayDate(this.ownerDetails.last_login_at, this.DATETIME_SHORT);
        },
        selectedActivityModuleOptions() {
            return this.activityModules.filter((module) => this.selectedActivityModules.includes(module.key));
        },
        activityRows() {
            return [...(this.ownerActivity?.months || [])].reverse();
        },
        activityChartRows() {
            return this.ownerActivity?.months || [];
        },
        activityTotal() {
            return this.activityRows.reduce((total, month) => total + (month.total || 0), 0);
        },
        activityChartOption() {
            return {
                color: this.chartColors,
                tooltip: {
                    trigger: "axis",
                    axisPointer: {
                        type: this.showCurveChart ? "line" : "shadow",
                    },
                    valueFormatter: (value) => `${value} activité(s)`,
                },
                legend: {
                    bottom: 0,
                    icon: "roundRect",
                    itemWidth: 12,
                    itemHeight: 8,
                    textStyle: {
                        color: "#435568",
                        fontSize: 12,
                    },
                },
                grid: {
                    top: 18,
                    right: 12,
                    bottom: 56,
                    left: 42,
                },
                xAxis: {
                    type: "category",
                    data: this.activityChartRows.map((month) => this.shortMonthLabel(month.label)),
                    axisTick: {
                        alignWithLabel: true,
                    },
                    axisLabel: {
                        color: "#627184",
                    },
                    axisLine: {
                        lineStyle: {
                            color: "#d9e4ec",
                        },
                    },
                },
                yAxis: {
                    type: "value",
                    minInterval: 1,
                    axisLabel: {
                        color: "#627184",
                    },
                    splitLine: {
                        lineStyle: {
                            color: "#edf3f7",
                        },
                    },
                },
                series: this.activityChartSeries,
            };
        },
        chartColors() {
            return this.selectedActivityModuleOptions.map((module) => module.color);
        },
        activityChartSeries() {
            return this.selectedActivityModuleOptions.map((module) => {
                if (this.showCurveChart) {
                    return {
                        name: module.label,
                        type: "line",
                        smooth: true,
                        symbolSize: 7,
                        lineStyle: {
                            width: 3,
                            color: module.color,
                        },
                        itemStyle: {
                            color: module.color,
                        },
                        emphasis: {
                            focus: "series",
                        },
                        data: this.activityChartRows.map((month) => this.activityCountForModule(month, module.key)),
                    };
                }

                return {
                    name: module.label,
                    type: "bar",
                    stack: "activity",
                    barMaxWidth: 44,
                    emphasis: {
                        focus: "series",
                    },
                    data: this.activityChartRows.map((month) => this.activityCountForModule(month, module.key)),
                };
            });
        },
    },
    methods: {
        isSectionOpen(section) {
            return Boolean(this.openSections[section]);
        },
        toggleSection(section) {
            this.$set(this.openSections, section, !this.isSectionOpen(section));
        },
        toggleActivityModule(module) {
            if (this.selectedActivityModules.includes(module)) {
                if (this.selectedActivityModules.length === 1) {
                    return;
                }

                this.selectedActivityModules = this.selectedActivityModules.filter((selectedModule) => selectedModule !== module);
            } else {
                this.selectedActivityModules = [...this.selectedActivityModules, module];
            }

            this.fetchOwnerActivity();
        },
        activityCountForModule(month, module) {
            return month.modules?.[module] || 0;
        },
        shortMonthLabel(label) {
            if (!label) {
                return "";
            }

            const [month, year] = label.split(" ");

            return `${month.substring(0, 3)}. ${year}`;
        },
        onSubscriptionCanceled() {
            this.fetchSubscriptions();
            this.fetchOwnerDetails();
        },
        onSubscriptionAssigned() {
            this.fetchSubscriptions();
            this.fetchOwnerDetails();
        },
        toggleArchiveAccount() {
            if (this.ownerDetails.enabled_by_admin) {
                this.disableArchiveAccount();
            } else {
                this.enableArchiveAccount();
            }
        },
        enableArchiveAccount() {
            if (this.ownerDetails === null || this.isTogglingArchiveAccount) {
                return;
            }

            if (!confirm("Êtes vous sûr de vouloir réactiver ce compte de façon temporaire ?")) {
                return;
            }

            this.isTogglingArchiveAccount = true;

            axios
                .post(`/api/admin/archive/${this.owner_id}/enable`)
                .then((response) => {
                    this.notifySuccess(response);
                    this.fetchOwnerDetails();
                    this.fetchSubscriptions();
                })
                .catch((error) => {
                    this.notifyError(error);
                })
                .finally(() => {
                    this.isTogglingArchiveAccount = false;
                });
        },
        disableArchiveAccount() {
            if (this.ownerDetails === null || this.isTogglingArchiveAccount) {
                return;
            }

            if (!confirm("Êtes vous sûr de vouloir désactiver ce compte ?")) {
                return;
            }

            this.isTogglingArchiveAccount = true;

            axios
                .post(`/api/admin/archive/${this.owner_id}/disable`)
                .then((response) => {
                    this.notifySuccess(response);
                    this.fetchOwnerDetails();
                    this.fetchSubscriptions();
                })
                .catch((error) => {
                    this.notifyError(error);
                })
                .finally(() => {
                    this.isTogglingArchiveAccount = false;
                });
        },
        fetchOwnerDetails() {
            this.loading++;

            axios
                .get(`/api/admin/owners/${this.owner_id}?include=restaurants.subscriptions.module,has_active_subscription,employees,accepted_cgvs`)
                .then((response) => {
                    this.loading--;
                    this.ownerDetails = response.data;
                })
                .catch((error) => {
                    this.loading--;
                    this.error = this.getErrorMsgFromErrorResponse(error);
                });
        },
        fetchSubscriptions() {
            this.loading++;

            axios
                .get(`/api/stripe/licences?owner_id=${this.owner_id}`)
                .then((response) => {
                    this.loading--;
                    this.stripeSubscriptions = response.data.data;
                })
                .catch((error) => {
                    this.loading--;
                    this.error = this.getErrorMsgFromErrorResponse(error);
                });
        },
        fetchSubscriptionsInvoices() {
            this.loading++;

            axios
                .get(`/api/stripe/invoices?type=subscription&owner_id=${this.owner_id}`)
                .then((response) => {
                    this.loading--;
                    this.stripeSubscriptionsInvoices = response.data.data;
                })
                .catch((error) => {
                    this.loading--;
                    this.error = this.getErrorMsgFromErrorResponse(error);
                });
        },
        fetchOwnerActivity() {
            this.ownerActivityLoading = true;

            axios
                .get(`/api/admin/owners/${this.owner_id}/activity?months=${this.activityMonths}&modules=${this.selectedActivityModules.join(",")}`)
                .then((response) => {
                    this.ownerActivity = response.data.data;
                })
                .catch((error) => {
                    this.notifyError(error);
                })
                .finally(() => {
                    this.ownerActivityLoading = false;
                });
        },
    },
    components: {
        LoaderComponent,
        ShowOwnerDetails,
        ShowSubscriptionDetails,
        ShowOwnerRestaurants,
        ShowOwnerEmployees,
        ShowOwnerAcceptedCgvs,
        ShowOwnerSmsList,
        Invoices,
        EditOwner,
        VChart,
    },
    created() {
        this.fetchOwnerDetails();
        this.fetchSubscriptions();
        this.fetchOwnerActivity();
    },
};
</script>

<style>
.admin-owner-details {
    width: 100%;
    max-width: 1360px;
    margin-right: auto;
    margin-left: auto;
}

.admin-owner-details-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.owner-details-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    padding: 8px 2px 2px;
}

.owner-details-nav {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 10px;
}

.owner-details-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #607287;
    font-size: 0.82rem;
    font-weight: 600;
}

.owner-details-back .feather {
    width: 15px;
    height: 15px;
}

.owner-details-kicker {
    display: inline-flex;
    color: #157ec2;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

.owner-details-title-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
}

.owner-details-title-row .title {
    color: #16273a;
    font-size: 1.45rem;
    font-weight: 700;
    line-height: 1.2;
}

.owner-details-email {
    display: inline-flex;
    margin-top: 6px;
    color: #157ec2;
    word-break: break-word;
}

.owner-details-status {
    font-size: 0.7rem;
    letter-spacing: 0;
}

.owner-details-header-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 30px;
}

.owner-details-header-actions .feather {
    width: 14px;
    height: 14px;
    margin-right: 4px;
}

.owner-details-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}

.owner-details-summary-card {
    padding: 14px 16px;
    border: 1px solid #d9e4ec;
    border-radius: 6px;
    background: #fff;
}

.owner-details-summary-card span {
    display: block;
    color: #627184;
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
}

.owner-details-summary-card strong {
    display: block;
    margin-top: 6px;
    color: #182b3c;
    font-size: 1.35rem;
    line-height: 1.1;
}

.owner-details-summary-card small {
    display: block;
    margin-top: 6px;
    color: #607287;
}

.owner-details-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 14px;
    border: 1px solid #e6a55f;
    border-radius: 6px;
    background: #fffaf3;
    color: #9a5b12;
    font-weight: 600;
}

.owner-details-notice .feather {
    width: 17px;
    height: 17px;
}

.owner-details-card {
    padding: 16px;
    border: 1px solid #d9e4ec;
    border-radius: 6px;
    background: #fff;
}

.owner-details-collapsible {
    padding: 0;
    overflow: hidden;
}

.owner-details-section-toggle,
.owner-details-section-header {
    display: flex;
    width: 100%;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}

.owner-details-section-toggle {
    padding: 16px;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    text-align: left;
}

.owner-details-section-toggle:focus {
    outline: 2px solid #9fd2f0;
    outline-offset: -2px;
}

.owner-details-section-toggle:hover {
    background: #f8fbfd;
}

.owner-details-section-header {
    margin-bottom: 14px;
}

.owner-details-section-toggle h6,
.owner-details-section-header h6 {
    margin-bottom: 4px;
    color: #182b3c;
    font-size: 1rem;
    font-weight: 700;
}

.owner-details-section-toggle p,
.owner-details-section-header p {
    color: #607287;
    font-size: 0.86rem;
}

.owner-details-section-toggle .feather,
.owner-details-section-header .feather {
    width: 15px;
    height: 15px;
    margin-right: 4px;
}

.owner-details-toggle-meta {
    display: flex;
    align-items: center;
    gap: 10px;
}

.owner-details-chevron {
    flex: none;
    margin-right: 0 !important;
    color: #607287;
    transition: transform 0.18s ease;
}

.owner-details-chevron.open {
    transform: rotate(180deg);
}

.owner-details-section-body {
    padding: 0 16px 16px;
}

.owner-details-count {
    display: inline-flex;
    min-width: 30px;
    height: 30px;
    align-items: center;
    justify-content: center;
    border-radius: 15px;
    background: #eef7fc;
    color: #157ec2;
    font-weight: 700;
}

.owner-details-empty {
    padding: 18px;
    border: 1px dashed #ced8e1;
    border-radius: 6px;
    color: #627184;
    text-align: center;
}

.owner-details-subscriptions {
    display: grid;
    gap: 12px;
}

.owner-details-subscription {
    padding: 14px;
    border: 1px solid #e2ebf1;
    border-radius: 6px;
    background: #fbfdff;
}

.owner-details-scroll {
    overflow-x: auto;
}

.owner-activity-toolbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.owner-activity-modules {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.owner-activity-module {
    padding: 6px 10px;
    border: 1px solid #d9e4ec;
    border-radius: 16px;
    background: #fff;
    color: #435568;
    font-size: 0.82rem;
    font-weight: 700;
}

.owner-activity-module.active {
    border-color: #157ec2;
    background: #eef7fc;
    color: #157ec2;
}

.owner-activity-actions {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
}

.owner-activity-curve-toggle {
    display: inline-flex;
    flex: none;
    align-items: center;
    gap: 8px;
    min-height: 32px;
    margin-bottom: 0;
    padding: 6px 10px;
    border: 1px solid #d9e4ec;
    border-radius: 16px;
    background: #fff;
    color: #435568;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 700;
}

.owner-activity-curve-toggle input {
    margin: 0;
}

.owner-activity-months {
    flex: none;
    width: 120px;
}

.owner-activity-chart-card {
    height: 320px;
    padding: 12px;
    border: 1px solid #e2ebf1;
    border-radius: 6px;
    background: #fbfdff;
}

.owner-activity-chart {
    width: 100%;
    height: 100%;
}

.owner-details-card table {
    margin-bottom: 0;
}

.owner-details-card thead th {
    border-top: 0;
    background: #f4f8fb;
    color: #435568;
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
}

.owner-details-card tbody td {
    vertical-align: middle;
}

@media (max-width: 991.98px) {
    .owner-details-header {
        flex-direction: column;
    }

    .owner-details-header-actions {
        justify-content: flex-start;
        padding-top: 0;
    }

    .owner-details-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .owner-activity-toolbar {
        flex-direction: column;
    }
}

@media (max-width: 575.98px) {
    .owner-details-summary {
        grid-template-columns: 1fr;
    }
}
</style>
